<template>
  <div class="about">
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-700 text-white py-20" style="margin: -24px -24px 24px -24px; border-radius: 8px 8px 0 0;">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 class="text-4xl md:text-5xl font-bold mb-6">About Vue 3 + Vite</h1>
        <p class="text-xl md:text-2xl max-w-3xl mx-auto opacity-90">
          Modern web development with the latest technologies and best practices
        </p>
      </div>
    </section>

    <!-- Content Sections -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <!-- Vue 3 Section -->
      <section class="mb-20">
        <div class="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 class="text-3xl font-bold text-gray-900 mb-6">Vue 3</h2>
            <p class="text-lg text-gray-600 mb-6">
              Vue 3 is the latest major version of Vue.js, featuring a completely rewritten reactivity system,
              Composition API, and improved performance.
            </p>
            <div class="space-y-4">
              <div class="flex items-start space-x-3">
                <div class="p-2 bg-green-100 rounded-lg">
                  <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-900">Composition API</h3>
                  <p class="text-gray-600">Better logic reuse and code organization</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="p-2 bg-green-100 rounded-lg">
                  <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                  </svg>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-900">Performance</h3>
                  <p class="text-gray-600">Faster rendering and smaller bundle sizes</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="p-2 bg-green-100 rounded-lg">
                  <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4-4-4 4"/>
                  </svg>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-900">TypeScript Support</h3>
                  <p class="text-gray-600">First-class TypeScript integration</p>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gradient-to-br from-green-400 to-green-600 p-8 rounded-lg text-white">
            <div class="text-6xl font-bold mb-4">3.4+</div>
            <div class="text-xl">Latest Version</div>
          </div>
        </div>
      </section>

      <!-- Vite Section -->
      <section class="mb-20">
        <div class="grid md:grid-cols-2 gap-12 items-center">
          <div class="order-2 md:order-1">
            <div class="bg-gradient-to-br from-purple-400 to-purple-600 p-8 rounded-lg text-white">
              <div class="text-6xl font-bold mb-4">5.1+</div>
              <div class="text-xl">Latest Version</div>
            </div>
          </div>
          <div class="order-1 md:order-2">
            <h2 class="text-3xl font-bold text-gray-900 mb-6">Vite</h2>
            <p class="text-lg text-gray-600 mb-6">
              Vite is a modern build tool that provides a fast development experience and optimized production builds.
            </p>
            <div class="space-y-4">
              <div class="flex items-start space-x-3">
                <div class="p-2 bg-purple-100 rounded-lg">
                  <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                  </svg>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-900">Lightning Fast HMR</h3>
                  <p class="text-gray-600">Instant hot module replacement</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="p-2 bg-purple-100 rounded-lg">
                  <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                  </svg>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-900">Optimized Builds</h3>
                  <p class="text-gray-600">Efficient code splitting and bundling</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <div class="p-2 bg-purple-100 rounded-lg">
                  <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"/>
                  </svg>
                </div>
                <div>
                  <h3 class="font-semibold text-gray-900">Rich Plugin Ecosystem</h3>
                  <p class="text-gray-600">Extensible with community plugins</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Tech Stack Section -->
      <section class="mb-20">
        <h2 class="text-3xl font-bold text-gray-900 mb-8 text-center">Technology Stack</h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div
            v-for="tech in techStack"
            :key="tech.name"
            class="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 text-center"
          >
            <div class="text-4xl mb-3">{{ tech.icon }}</div>
            <h3 class="font-semibold text-gray-900">{{ tech.name }}</h3>
            <p class="text-sm text-gray-600 mt-2">{{ tech.description }}</p>
          </div>
        </div>
      </section>

      <!-- Benefits Section -->
      <section class="bg-gray-50 p-8 rounded-lg">
        <h2 class="text-3xl font-bold text-gray-900 mb-8 text-center">Why Choose Vue 3 + Vite?</h2>
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div
            v-for="benefit in benefits"
            :key="benefit.title"
            class="text-center"
          >
            <div class="text-5xl mb-4">{{ benefit.icon }}</div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">{{ benefit.title }}</h3>
            <p class="text-gray-600">{{ benefit.description }}</p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const techStack = ref([
  {
    name: 'Vue 3',
    icon: '🟢',
    description: 'Progressive JavaScript framework'
  },
  {
    name: 'Vite',
    icon: '⚡',
    description: 'Next generation build tool'
  },
  {
    name: 'Pinia',
    icon: '🍍',
    description: 'Intuitive state management'
  },
  {
    name: 'Vue Router',
    icon: '🛣️',
    description: 'Official routing solution'
  },
  {
    name: 'Tailwind CSS',
    icon: '🎨',
    description: 'Utility-first CSS framework'
  },
  {
    name: 'PostCSS',
    icon: '📝',
    description: 'CSS transformation tool'
  },
  {
    name: 'ESLint',
    icon: '🔍',
    description: 'JavaScript linting utility'
  },
  {
    name: 'Prettier',
    icon: '💅',
    description: 'Code formatter'
  }
])

const benefits = ref([
  {
    icon: '🚀',
    title: 'Fast Development',
    description: 'Instant hot module replacement and lightning-fast builds'
  },
  {
    icon: '🎯',
    title: 'Modern Features',
    description: 'Latest JavaScript features and modern development patterns'
  },
  {
    icon: '📱',
    title: 'Mobile First',
    description: 'Responsive design optimized for all devices'
  },
  {
    icon: '🔧',
    title: 'Developer Experience',
    description: 'Excellent tooling and debugging capabilities'
  },
  {
    icon: '📊',
    title: 'Performance',
    description: 'Optimized for speed and efficiency'
  },
  {
    icon: '🛡️',
    title: 'Type Safety',
    description: 'Optional TypeScript support for better code quality'
  }
])
</script>
